import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";

import { makeStyles, createMuiTheme, ThemeProvider, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import {
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  Avatar,
  Dialog,
  DialogContent,
  DialogActions,
} from "@material-ui/core";
import CheckIcon from "@material-ui/icons/Check";

import { openApi } from "../../services/api";

const AVATAR_COLORS = ["#2563eb", "#ea580c", "#16a34a", "#7c3aed", "#db2777", "#0891b2", "#ca8a04"];
const WEEKDAYS = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];
const MONTHS = [
  "janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
];

const lighten = (hex, amount = 0.5) => {
  const h = (hex || "").replace("#", "");
  if (h.length !== 6) return hex;
  const n = parseInt(h, 16);
  const f = (v) => Math.round(v + (255 - v) * amount);
  return `#${[(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => f(v).toString(16).padStart(2, "0")).join("")}`;
};

const darken = (hex, amount = 0.25) => {
  const h = (hex || "").replace("#", "");
  if (h.length !== 6) return hex;
  const n = parseInt(h, 16);
  const f = (v) => Math.round(v * (1 - amount));
  return `#${[(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => f(v).toString(16).padStart(2, "0")).join("")}`;
};

const readableOn = (hex) => {
  const h = (hex || "").replace("#", "");
  if (h.length !== 6) return "#fff";
  const lin = (v) => {
    const c = parseInt(v, 16) / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  const L = 0.2126 * lin(h.slice(0, 2)) + 0.7152 * lin(h.slice(2, 4)) + 0.0722 * lin(h.slice(4, 6));
  return L > 0.55 ? "#1f2937" : "#fff";
};

const initials = (name = "") =>
  name.trim().split(/\s+/).slice(0, 2).map((p) => p[0] || "").join("").toUpperCase();

const formatPrice = (value) =>
  Number(value || 0) > 0
    ? Number(value).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    : "Grátis";

const formatDuration = (minutes) => {
  const m = Number(minutes) || 0;
  if (m < 60) return `${m} min`;
  const rest = m % 60;
  return rest ? `${Math.floor(m / 60)}h${String(rest).padStart(2, "0")}` : `${m / 60} h`;
};

// "qua, 05/11"
const formatShortDate = (ymd) => {
  const d = moment(ymd, "YYYY-MM-DD");
  return `${WEEKDAYS[d.day()]}, ${d.format("DD/MM")}`;
};

// "(41) 99826-6105"
const formatPhone = (digits) => {
  const d = digits.slice(0, 11);
  if (d.length <= 2) return d.length ? `(${d}` : "";
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
};

const PERIODS = [
  ["Manhã", (t) => t < "12:00"],
  ["Tarde", (t) => t >= "12:00" && t < "18:00"],
  ["Noite", (t) => t >= "18:00"],
];

const ERROR_MESSAGES = {
  ERR_SLOT_NOT_AVAILABLE: "Este horário acabou de ser preenchido. Escolha outro, por favor.",
  ERR_BOOKING_INVALID_NAME: "Informe seu nome usando só letras (até 60 caracteres).",
  ERR_BOOKING_INVALID_NUMBER: "Confira o WhatsApp: DDD e número.",
  ERR_BOOKING_LIMIT_PER_NUMBER:
    "Este número já tem agendamentos marcados. Para marcar outro, fale com a gente pelo WhatsApp.",
  ERR_TOO_MANY_REQUESTS: "Muitas tentativas seguidas. Aguarde alguns minutos e tente de novo.",
};

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  // fundo dos botões de escolha: no escuro, um tom abaixo do cartão
  const optionBg = dark ? "#0f172a" : theme.palette.background.paper;
  return {
    root: {
      minHeight: "100vh",
      backgroundColor: dark ? "#0f172a" : "#f0f2f5",
      paddingTop: theme.spacing(4),
      paddingBottom: theme.spacing(4),
      color: theme.palette.text.primary,
      [theme.breakpoints.down("xs")]: { paddingTop: 0, paddingBottom: 0 },
    },
    container: {
      [theme.breakpoints.down("xs")]: { paddingLeft: 0, paddingRight: 0 },
    },
    card: {
      borderRadius: 18,
      overflow: "hidden",
      backgroundColor: dark ? "#1e293b" : "#fff",
      [theme.breakpoints.down("xs")]: { borderRadius: 0, minHeight: "100vh", display: "flex", flexDirection: "column" },
    },
    hero: { padding: theme.spacing(2.5, 3, 3), [theme.breakpoints.down("xs")]: { padding: theme.spacing(2.5, 2) } },
    brand: { display: "flex", alignItems: "center", gap: theme.spacing(1.75) },
    logo: {
      width: 56,
      height: 56,
      borderRadius: 14,
      background: "#fff",
      display: "grid",
      placeItems: "center",
      fontWeight: 800,
      fontSize: "1.2rem",
      flexShrink: 0,
      overflow: "hidden",
      boxShadow: "0 4px 14px rgba(0,0,0,.18)",
    },
    logoImg: { width: "100%", height: "100%", objectFit: "contain", padding: 6 },
    brandName: { fontWeight: 700, lineHeight: 1.1, letterSpacing: "-0.3px" },
    body: { padding: theme.spacing(0.5, 3, 2), flex: 1, [theme.breakpoints.down("xs")]: { padding: theme.spacing(0.5, 2, 2) } },
    step: {
      padding: theme.spacing(2, 0),
      borderBottom: `1px solid ${theme.palette.divider}`,
      "&:last-child": { borderBottom: "none" },
    },
    stepHead: { display: "flex", alignItems: "center", gap: 10, marginBottom: theme.spacing(1.5) },
    stepNum: {
      width: 24,
      height: 24,
      borderRadius: "50%",
      display: "grid",
      placeItems: "center",
      fontSize: 12,
      fontWeight: 700,
      flexShrink: 0,
    },
    stepTitle: { fontWeight: 700, fontSize: 15 },
    stepAside: { marginLeft: "auto", fontSize: 12.5, color: theme.palette.text.secondary },
    serviceList: { display: "grid", gap: 8 },
    serviceOpt: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      width: "100%",
      textAlign: "left",
      font: "inherit",
      color: "inherit",
      border: `1.5px solid ${theme.palette.divider}`,
      borderRadius: 12,
      padding: theme.spacing(1.5, 1.75),
      background: optionBg,
      cursor: "pointer",
    },
    days: {
      display: "flex",
      gap: 6,
      overflowX: "auto",
      paddingBottom: 6,
      scrollbarWidth: "thin",
    },
    day: {
      flex: "0 0 58px",
      border: `1.5px solid ${theme.palette.divider}`,
      borderRadius: 12,
      padding: "8px 0",
      background: optionBg,
      color: theme.palette.text.primary,
      font: "inherit",
      textAlign: "center",
      cursor: "pointer",
      "&:disabled": { cursor: "default", color: theme.palette.text.disabled },
    },
    dayWeek: { display: "block", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em", opacity: 0.75 },
    dayNum: { display: "block", fontSize: 18, fontWeight: 700, fontVariantNumeric: "tabular-nums", margin: "2px 0" },
    dayDot: { display: "block", width: 6, height: 6, borderRadius: "50%", margin: "0 auto" },
    provTabs: { display: "flex", gap: 6, flexWrap: "wrap", marginBottom: theme.spacing(1.5) },
    provTab: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      font: "inherit",
      fontSize: 13,
      fontWeight: 600,
      border: `1.5px solid ${theme.palette.divider}`,
      borderRadius: 999,
      padding: "4px 12px 4px 4px",
      background: optionBg,
      color: theme.palette.text.primary,
      cursor: "pointer",
    },
    provAny: { padding: "5px 12px" },
    provAvatar: { width: 24, height: 24, fontSize: 10, fontWeight: 700 },
    period: {
      fontSize: 12,
      fontWeight: 600,
      color: theme.palette.text.secondary,
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      margin: theme.spacing(1.25, 0, 1),
    },
    slots: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(74px, 1fr))", gap: 8 },
    slot: {
      border: `1.5px solid ${theme.palette.divider}`,
      borderRadius: 10,
      padding: "9px 0",
      background: optionBg,
      color: theme.palette.text.primary,
      font: "inherit",
      fontWeight: 700,
      fontSize: 14,
      fontVariantNumeric: "tabular-nums",
      cursor: "pointer",
    },
    muted: { color: theme.palette.text.secondary, fontSize: 13.5 },
    summary: {
      position: "sticky",
      bottom: 0,
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: theme.spacing(1.75, 3),
      paddingBottom: `calc(${theme.spacing(1.75)}px + env(safe-area-inset-bottom, 0px))`,
      borderTop: `1px solid ${theme.palette.divider}`,
      backgroundColor: dark ? "#1e293b" : "#fff",
      [theme.breakpoints.down("xs")]: { paddingLeft: theme.spacing(2), paddingRight: theme.spacing(2) },
    },
    summaryText: { flex: 1, minWidth: 0 },
    brandButton: {
      textTransform: "none",
      fontWeight: 700,
      borderRadius: 10,
      padding: "9px 18px",
      "&:disabled": { opacity: 0.45 },
    },
    center: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      padding: theme.spacing(4, 3),
      textAlign: "center",
    },
    okIcon: {
      width: 56,
      height: 56,
      borderRadius: "50%",
      display: "grid",
      placeItems: "center",
      marginBottom: theme.spacing(1.25),
      color: dark ? "#81c784" : "#2e7d32",
      backgroundColor: dark ? "rgba(129,199,132,.16)" : "#e6f4e7",
    },
    recap: {
      width: "100%",
      maxWidth: 380,
      marginTop: theme.spacing(2),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 12,
      textAlign: "left",
      "& > div": {
        display: "flex",
        justifyContent: "space-between",
        gap: 10,
        padding: theme.spacing(1.25, 1.75),
        borderBottom: `1px solid ${theme.palette.divider}`,
        fontSize: 14,
      },
      "& > div:last-child": { borderBottom: "none" },
      "& span": { color: theme.palette.text.secondary },
    },
    dialogPaper: { borderRadius: 16 },
    dialogHero: { padding: "18px 24px 16px" },
    dialogActions: { padding: "8px 24px 20px", gap: 8 },
  };
});

// ── Conteúdo (recebe o tema claro/escuro do sistema) ─────────────────────────
const PublicBookingContent = () => {
  const classes = useStyles();
  const muiTheme = useTheme();
  const prefersDark = muiTheme.palette.type === "dark";
  const { slug } = useParams();

  const [loadingBusiness, setLoadingBusiness] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [business, setBusiness] = useState(null);

  const [serviceId, setServiceId] = useState(null);
  const [days, setDays] = useState([]);
  const [loadingDays, setLoadingDays] = useState(false);
  const [date, setDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [providerFilter, setProviderFilter] = useState("any");
  const [selected, setSelected] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [contactName, setContactName] = useState("");
  const [phoneDigits, setPhoneDigits] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const company = business ? business.company : null;
  const services = business ? business.services : [];
  const showPrices = company ? company.showPrices !== false : true;
  const chooseProvider = company ? company.chooseProvider !== false : true;
  const selectedService = services.find((s) => s.id === serviceId) || null;

  const brandColors = company && company.brandColors ? company.brandColors : [];
  const primary = brandColors[0] || "#2563eb";
  const gradientEnd = brandColors[1] || darken(primary, 0.3);
  const brandGradient = `linear-gradient(135deg, ${primary}, ${gradientEnd})`;
  const onPrimary = readableOn(primary);
  // cor da marca legível sobre o fundo do cartão
  const accent = prefersDark ? lighten(primary, 0.45) : onPrimary === "#fff" ? primary : darken(primary, 0.4);
  const providerPalette = brandColors.length ? brandColors : AVATAR_COLORS;
  const providerColor = (id) => providerPalette[Math.abs(Number(id) || 0) % providerPalette.length];
  const selectedStyle = { backgroundColor: primary, borderColor: primary, color: onPrimary };
  const outlineStyle = { borderColor: accent, boxShadow: `0 0 0 1px ${accent}` };

  const logoUrl = company && company.logo ? `${process.env.REACT_APP_BACKEND_URL}/public/${company.logo}` : null;

  useEffect(() => {
    const fetchBusiness = async () => {
      try {
        const { data } = await openApi.get(`/public/booking/${slug}`);
        setBusiness(data);
        if (data.services && data.services.length === 1) setServiceId(data.services[0].id);
      } catch (err) {
        setNotFound(true);
      } finally {
        setLoadingBusiness(false);
      }
    };
    fetchBusiness();
  }, [slug]);

  // Dias com vaga para o serviço escolhido (próximos 14 dias).
  useEffect(() => {
    if (!serviceId) return;
    let cancelled = false;
    const fetchDays = async () => {
      setLoadingDays(true);
      setDays([]);
      setDate(null);
      setSelected(null);
      try {
        const { data } = await openApi.get(`/public/booking/${slug}/days`, { params: { serviceId } });
        if (cancelled) return;
        const list = data.days || [];
        setDays(list);
        const first = list.find((d) => d.slots > 0);
        setDate(first ? first.date : list[0] ? list[0].date : null);
      } catch (err) {
        if (!cancelled) setDays([]);
      } finally {
        if (!cancelled) setLoadingDays(false);
      }
    };
    fetchDays();
    return () => {
      cancelled = true;
    };
  }, [slug, serviceId]);

  const fetchSlots = useCallback(async () => {
    if (!serviceId || !date) {
      setSlots([]);
      return;
    }
    setLoadingSlots(true);
    setSelected(null);
    try {
      const { data } = await openApi.get(`/public/booking/${slug}/slots`, { params: { serviceId, date } });
      setSlots(data.slots || []);
    } catch (err) {
      setSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  }, [slug, serviceId, date]);

  useEffect(() => {
    fetchSlots();
  }, [fetchSlots]);

  const providers = useMemo(() => {
    const map = new Map();
    slots.forEach((s) => {
      if (!map.has(s.userId)) map.set(s.userId, { id: s.userId, name: s.userName });
    });
    return Array.from(map.values());
  }, [slots]);

  const showProviderTabs = chooseProvider && providers.length > 1;

  // Um botão por horário: no modo "qualquer profissional" junta os horários
  // iguais e usa o primeiro profissional livre.
  const visibleSlots = useMemo(() => {
    const source =
      showProviderTabs && providerFilter !== "any"
        ? slots.filter((s) => String(s.userId) === String(providerFilter))
        : slots;
    const byTime = new Map();
    source.forEach((s) => {
      if (!byTime.has(s.time)) byTime.set(s.time, s);
    });
    return Array.from(byTime.values()).sort((a, b) => a.startAt.localeCompare(b.startAt));
  }, [slots, providerFilter, showProviderTabs]);

  const selectService = (id) => {
    if (id === serviceId) return;
    setServiceId(id);
    setProviderFilter("any");
    setSelected(null);
  };

  const selectDate = (ymd) => {
    setDate(ymd);
    setProviderFilter("any");
    setSelected(null);
  };

  const handleSubmit = async () => {
    const name = contactName.replace(/\s+/g, " ").trim();
    if (!selected || name.length < 2 || phoneDigits.length < 10) {
      toast.error("Preencha seu nome e WhatsApp com DDD.");
      return;
    }
    setSubmitting(true);
    try {
      await openApi.post(`/public/booking/${slug}`, {
        serviceId,
        userId: selected.userId,
        startAt: selected.startAt,
        contactName: name,
        contactNumber: `55${phoneDigits}`,
      });
      setConfirmOpen(false);
      setDone({ service: selectedService, slot: selected, date });
    } catch (err) {
      const code = err && err.response && err.response.data ? err.response.data.error : null;
      toast.error(ERROR_MESSAGES[code] || "Não foi possível concluir o agendamento. Tente novamente.");
      if (code === "ERR_SLOT_NOT_AVAILABLE") {
        setConfirmOpen(false);
        fetchSlots();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const hero = (
    <div className={classes.hero} style={{ background: brandGradient, color: onPrimary }}>
      <div className={classes.brand}>
        <div className={classes.logo} style={{ color: primary }}>
          {logoUrl ? (
            <img src={logoUrl} alt={company.name} className={classes.logoImg} />
          ) : (
            initials(company ? company.name : "")
          )}
        </div>
        <div style={{ minWidth: 0 }}>
          <Typography variant="h5" component="h1" className={classes.brandName} style={{ color: onPrimary }}>
            {company ? company.name : ""}
          </Typography>
          <Typography variant="body2" style={{ color: onPrimary, opacity: 0.85 }}>
            Agende seu horário online
          </Typography>
        </div>
      </div>
    </div>
  );

  if (loadingBusiness) {
    return (
      <div className={classes.root}>
        <div className={classes.center}>
          <CircularProgress />
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className={classes.root}>
        <Container maxWidth="sm">
          <Paper className={classes.card}>
            <div className={classes.center}>
              <Typography variant="h6">Página não encontrada</Typography>
              <Typography variant="body2" color="textSecondary">
                Confira o link de agendamento.
              </Typography>
            </div>
          </Paper>
        </Container>
      </div>
    );
  }

  if (done) {
    return (
      <div className={classes.root}>
        <Container maxWidth="sm" className={classes.container}>
          <Paper className={classes.card} elevation={1}>
            {hero}
            <div className={classes.center}>
              <div className={classes.okIcon}>
                <CheckIcon style={{ fontSize: 30 }} />
              </div>
              <Typography variant="h6">Agendamento confirmado</Typography>
              <Typography variant="body2" color="textSecondary">
                Enviamos a confirmação no seu WhatsApp.
              </Typography>
              <div className={classes.recap}>
                <div>
                  <span>Serviço</span>
                  <strong>{done.service ? done.service.name : ""}</strong>
                </div>
                <div>
                  <span>Quando</span>
                  <strong>{`${formatShortDate(done.date)} às ${done.slot.time}`}</strong>
                </div>
                {chooseProvider && (
                  <div>
                    <span>Com</span>
                    <strong>{done.slot.userName}</strong>
                  </div>
                )}
                {showPrices && done.service && done.service.price !== null && (
                  <div>
                    <span>Valor</span>
                    <strong>{formatPrice(done.service.price)}</strong>
                  </div>
                )}
              </div>
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 14 }}>
                Precisa remarcar? Responda a mensagem no WhatsApp.
              </Typography>
            </div>
          </Paper>
        </Container>
      </div>
    );
  }

  const monthLabel = (() => {
    const visible = days.slice(0, 7);
    if (!visible.length) return "";
    const months = Array.from(new Set(visible.map((d) => moment(d.date, "YYYY-MM-DD").month())));
    return months.map((m) => MONTHS[m]).join(" / ");
  })();

  const stepNumStyle = { backgroundColor: primary, color: onPrimary };
  const canContinue = Boolean(selected);

  return (
    <div className={classes.root}>
      <Container maxWidth="sm" className={classes.container}>
        <Paper className={classes.card} elevation={1}>
          {hero}
          <div className={classes.body}>
            {services.length === 0 ? (
              <Typography className={classes.muted} style={{ padding: "20px 0" }}>
                Nenhum serviço disponível para agendamento no momento.
              </Typography>
            ) : (
              <>
                <section className={classes.step} aria-labelledby="step-service">
                  <div className={classes.stepHead}>
                    <span className={classes.stepNum} style={stepNumStyle}>1</span>
                    <span id="step-service" className={classes.stepTitle}>Serviço</span>
                  </div>
                  <div className={classes.serviceList}>
                    {services.map((s) => {
                      const on = s.id === serviceId;
                      return (
                        <button
                          key={s.id}
                          type="button"
                          className={classes.serviceOpt}
                          style={on ? outlineStyle : undefined}
                          aria-pressed={on}
                          onClick={() => selectService(s.id)}
                        >
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600 }}>{s.name}</div>
                            <div className={classes.muted} style={{ fontSize: 12.5 }}>
                              {formatDuration(s.durationMinutes)}
                            </div>
                          </div>
                          {showPrices && s.price !== null && (
                            <strong style={{ fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                              {formatPrice(s.price)}
                            </strong>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </section>

                {selectedService && (
                  <section className={classes.step} aria-labelledby="step-day">
                    <div className={classes.stepHead}>
                      <span className={classes.stepNum} style={stepNumStyle}>2</span>
                      <span id="step-day" className={classes.stepTitle}>Dia</span>
                      <span className={classes.stepAside}>{monthLabel}</span>
                    </div>
                    {loadingDays ? (
                      <CircularProgress size={22} />
                    ) : days.length === 0 ? (
                      <Typography className={classes.muted}>Não foi possível carregar os dias. Tente de novo.</Typography>
                    ) : (
                      <div className={classes.days} role="group" aria-label="Escolha o dia">
                        {days.map((d) => {
                          const m = moment(d.date, "YYYY-MM-DD");
                          const on = d.date === date;
                          const full = d.slots === 0;
                          return (
                            <button
                              key={d.date}
                              type="button"
                              className={classes.day}
                              style={on ? selectedStyle : undefined}
                              aria-pressed={on}
                              disabled={full}
                              title={full ? "Sem horários" : `${d.slots} horários livres`}
                              onClick={() => selectDate(d.date)}
                            >
                              <span className={classes.dayWeek}>{WEEKDAYS[m.day()]}</span>
                              <span className={classes.dayNum}>{m.date()}</span>
                              <span
                                className={classes.dayDot}
                                style={{
                                  backgroundColor: on
                                    ? onPrimary
                                    : full
                                    ? muiTheme.palette.divider
                                    : prefersDark
                                    ? "#81c784"
                                    : "#2e7d32",
                                }}
                              />
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {!loadingDays && days.length > 0 && days.every((d) => d.slots === 0) && (
                      <Typography className={classes.muted} style={{ marginTop: 8 }}>
                        Sem horários livres nos próximos 14 dias para este serviço.
                      </Typography>
                    )}
                  </section>
                )}

                {selectedService && date && (
                  <section className={classes.step} aria-labelledby="step-time">
                    <div className={classes.stepHead}>
                      <span className={classes.stepNum} style={stepNumStyle}>3</span>
                      <span id="step-time" className={classes.stepTitle}>Horário</span>
                      {!loadingSlots && (
                        <span className={classes.stepAside}>{formatShortDate(date)}</span>
                      )}
                    </div>

                    {loadingSlots ? (
                      <CircularProgress size={22} />
                    ) : slots.length === 0 ? (
                      <Typography className={classes.muted}>
                        Sem horários neste dia. Os dias com ponto verde têm vaga.
                      </Typography>
                    ) : (
                      <>
                        {showProviderTabs && (
                          <div className={classes.provTabs} role="group" aria-label="Profissional">
                            <button
                              type="button"
                              className={`${classes.provTab} ${classes.provAny}`}
                              style={providerFilter === "any" ? outlineStyle : undefined}
                              aria-pressed={providerFilter === "any"}
                              onClick={() => {
                                setProviderFilter("any");
                                setSelected(null);
                              }}
                            >
                              Qualquer profissional
                            </button>
                            {providers.map((p) => {
                              const on = String(providerFilter) === String(p.id);
                              return (
                                <button
                                  key={p.id}
                                  type="button"
                                  className={classes.provTab}
                                  style={on ? outlineStyle : undefined}
                                  aria-pressed={on}
                                  onClick={() => {
                                    setProviderFilter(p.id);
                                    setSelected(null);
                                  }}
                                >
                                  <Avatar
                                    className={classes.provAvatar}
                                    style={{ backgroundColor: providerColor(p.id), color: readableOn(providerColor(p.id)) }}
                                  >
                                    {initials(p.name)}
                                  </Avatar>
                                  {p.name.split(" ")[0]}
                                </button>
                              );
                            })}
                          </div>
                        )}
                        {PERIODS.map(([label, test]) => {
                          const list = visibleSlots.filter((s) => test(s.time));
                          if (!list.length) return null;
                          return (
                            <div key={label}>
                              <div className={classes.period}>{label}</div>
                              <div className={classes.slots}>
                                {list.map((s) => {
                                  const on = selected && selected.startAt === s.startAt && selected.userId === s.userId;
                                  return (
                                    <button
                                      key={`${s.startAt}-${s.userId}`}
                                      type="button"
                                      className={classes.slot}
                                      style={on ? selectedStyle : undefined}
                                      aria-pressed={Boolean(on)}
                                      onClick={() => setSelected(s)}
                                    >
                                      {s.time}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </>
                    )}
                  </section>
                )}
              </>
            )}
          </div>

          {services.length > 0 && (
            <div className={classes.summary}>
              <div className={classes.summaryText}>
                <div style={{ fontWeight: 700 }}>
                  {selected ? `${formatShortDate(date)} às ${selected.time}` : "Escolha um horário"}
                </div>
                <div className={classes.muted} style={{ fontSize: 12.5 }}>
                  {selectedService
                    ? [
                        selectedService.name,
                        selected && chooseProvider ? selected.userName : formatDuration(selectedService.durationMinutes),
                        showPrices && selectedService.price !== null && selected ? formatPrice(selectedService.price) : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")
                    : "Comece escolhendo o serviço"}
                </div>
              </div>
              <Button
                variant="contained"
                disableElevation
                className={classes.brandButton}
                style={canContinue ? { backgroundColor: primary, color: onPrimary } : undefined}
                disabled={!canContinue}
                onClick={() => setConfirmOpen(true)}
              >
                Continuar
              </Button>
            </div>
          )}
        </Paper>
      </Container>

      <Dialog
        open={confirmOpen && Boolean(selected)}
        onClose={() => !submitting && setConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        classes={{ paper: classes.dialogPaper }}
        PaperProps={{ style: prefersDark ? { backgroundColor: "#1e293b" } : undefined }}
      >
        <div className={classes.dialogHero} style={{ background: brandGradient, color: onPrimary }}>
          <Typography variant="h6" style={{ fontWeight: 700, color: onPrimary, lineHeight: 1.15 }}>
            Seus dados
          </Typography>
          {selected && selectedService && (
            <Typography variant="body2" style={{ color: onPrimary, opacity: 0.88, marginTop: 4 }}>
              {[
                selectedService.name,
                `${formatShortDate(date)} às ${selected.time}`,
                chooseProvider ? selected.userName.split(" ")[0] : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </Typography>
          )}
        </div>
        <DialogContent style={{ paddingTop: 16 }}>
          <TextField
            id="booking-contact-name"
            label="Seu nome"
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            variant="outlined"
            fullWidth
            margin="dense"
            autoFocus
            disabled={submitting}
            inputProps={{ maxLength: 60, autoComplete: "name" }}
          />
          <TextField
            id="booking-contact-phone"
            label="WhatsApp com DDD"
            value={formatPhone(phoneDigits)}
            onChange={(e) => setPhoneDigits(e.target.value.replace(/\D/g, "").replace(/^55(?=\d{10,11}$)/, "").slice(0, 11))}
            variant="outlined"
            fullWidth
            margin="dense"
            placeholder="(41) 99999-9999"
            disabled={submitting}
            inputProps={{ inputMode: "tel", autoComplete: "tel-national" }}
            InputProps={{ startAdornment: <InputAdornment position="start">+55</InputAdornment> }}
            helperText="A confirmação e os lembretes chegam neste número."
          />
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button onClick={() => setConfirmOpen(false)} disabled={submitting} style={{ textTransform: "none" }}>
            Voltar
          </Button>
          <Button
            variant="contained"
            disableElevation
            className={classes.brandButton}
            style={{ backgroundColor: primary, color: onPrimary }}
            disabled={submitting || contactName.trim().length < 2 || phoneDigits.length < 10}
            onClick={handleSubmit}
          >
            {submitting ? <CircularProgress size={20} style={{ color: onPrimary }} /> : "Confirmar"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

// ── Tema claro/escuro conforme o sistema do visitante ────────────────────────
const PublicBooking = () => {
  const prefersDark = useMediaQuery("(prefers-color-scheme: dark)");
  const theme = useMemo(
    () => createMuiTheme({ palette: { type: prefersDark ? "dark" : "light" } }),
    [prefersDark]
  );
  return (
    <ThemeProvider theme={theme}>
      <PublicBookingContent />
    </ThemeProvider>
  );
};

export default PublicBooking;
